import { useMutation, useQueryClient } from 'react-query';
import { useApiPost } from '../../../api';
import { useBuildVars } from '../../../config';
import { EndUserSearchResponse } from './exceptionDeal';

export interface SubmitForReviewRequest {
  acopEndUserId: string;
  addressId: number;
  addressLine1: string;
  addressLine2: string;
  addressLine3: string;
  addressType: string;
  businessType?: string;
  city: string;
  cmpEndCustomerId: string;
  coG_Approval: string;
  countryCode: string;
  customerNumber: string;
  customersetid: string;
  dealnumber: string;
  dealversion: number;
  discounttype: string;
  editor: string;
  editorNotes: string;
  endUserName: string;
  endUserNumber: string;
  erpAddressId: number;
  importReferenceId: number;
  ingramEndUserId: string;
  markHignPriority: boolean;
  marketSegmentType: string;
  postalCode: string;
  quoteNumber: string;
  resellerEndUserId: number;
  reveiwer?: string;
  reveiwerNotes?: string;
  skSbo: number;
  state: string;
  userAction: 'A' | 'R' | 'S';
}

export function useSubmitForReview() {
  const buildVars = useBuildVars();
  const queryClient = useQueryClient();
  const apiPost = useApiPost({ host: buildVars.VP_API_BASE });
  return useMutation(
    (request: SubmitForReviewRequest) =>
      apiPost(
        '/system.webapi.gds/api/v1/gdsCoreApiVendor/SaveResellerEndUserRelation',
        { ...request }
      ),
    {
      onSuccess: (_, { importReferenceId }) => {
        queryClient.invalidateQueries(
          '/system.webapi.gds/api/v1/GdsCoreApiExceptionBids/searchExceptionBids'
        );
        queryClient.invalidateQueries(
          `/system.webapi.gds/api/v1/GdsCoreApiDeals/${importReferenceId}`
        );
        queryClient.invalidateQueries(
          `/system.webapi.gds/api/v1/GdsCoreApiDeals/${importReferenceId}/filtered`
        );
      },
    }
  );
}

export const mapSubmitForReviewRequest = (params: {
  autoQuote: string;
  businessType: string;
  checked: boolean;
  coG_Approval: string;
  countryCode: string;
  customersetid: string;
  deal_id: number;
  dealnumber: string;
  dealversion: number;
  discounttype: string;
  edResellerId: string;
  importreferenceid: number;
  isautoquoterequired: boolean;
  notes: string;
  quoteNumber: string;
  record: EndUserSearchResponse | undefined;
  role: boolean | undefined;
  user: string;
  userAction: SubmitForReviewRequest['userAction'];
}) => {
  const {
    autoQuote,
    businessType,
    checked,
    coG_Approval,
    countryCode,
    customersetid,
    deal_id,
    dealnumber,
    dealversion,
    discounttype,
    edResellerId,
    importreferenceid,
    isautoquoterequired,
    notes,
    quoteNumber,
    record,
    role,
    user,
    userAction,
  } = params;

  const getAddressField = (
    field: keyof EndUserSearchResponse['address'][0],
    defaultValue: any = null
  ) => record?.address[0]?.[field] ?? defaultValue;

  const getEndUserField = (
    field: keyof EndUserSearchResponse,
    defaultValue: any = null
  ) => record?.[field] ?? defaultValue;

  return {
    acopEndUserId: String(getAddressField('acopEndUserId')),
    addressId: getAddressField('addressId', 0),
    addressLine1: String(getAddressField('addressLine1')),
    addressLine2: String(getAddressField('addressLine2')),
    addressLine3: String(getAddressField('addressLine3')),
    addressType: String(getAddressField('addressType')),
    autoQuote,
    businessType: businessType ?? '',
    city: String(getAddressField('city')),
    cmpEndCustomerId: String(getAddressField('cmpEndCustomerId')),
    coG_Approval,
    countryCode,
    customerNumber: edResellerId,
    customersetid,
    dealnumber,
    dealversion,
    discounttype,
    editor: user,
    editorNotes: notes,
    endUserName: String(getEndUserField('endUserName')),
    endUserNumber: String(getEndUserField('endUserId')),
    erpAddressId: getAddressField('erpAddressId', 0),
    importReferenceId: importreferenceid,
    ingramEndUserId: String(getAddressField('ingramEndUserId')),
    isautoquoterequired,
    markHignPriority: checked,
    marketSegmentType: String(getEndUserField('marketSegmentType')),
    postalCode: String(getAddressField('postalCode')),
    quoteNumber,
    resellerEndUserId: getAddressField('resellerEndUserId', 0),
    reveiwer: role ? user : '',
    skSbo: deal_id,
    state: String(getAddressField('state')),
    userAction,
  };
};
